import { Link } from 'react-router-dom';
import { ArrowLeft, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function Privacy() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Zap className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="text-lg font-bold">Luma</span>
          </Link>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Link>
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 pb-safe">
        <article className="prose prose-invert max-w-none">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">Privacy Policy</h1>
          <p className="text-muted-foreground mb-8">Last Updated: February 2026</p>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">1. Introduction</h2>
            <p className="text-muted-foreground leading-relaxed">
              Welcome to Luma ("we," "our," or "us"). We are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclosure, and safeguard your information when you use our mobile application "Luma: Focus Timer & Tasks" (the "App").
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">2. Information We Collect</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We collect information that you provide directly to us and information automatically collected when you use the App.
            </p>
            
            <h3 className="text-lg font-medium mb-2">A. Personal Information</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              When you register, we collect your email address, display name, and password (stored in encrypted format via our authentication provider). You may also choose to upload a profile picture.
            </p>

            <h3 className="text-lg font-medium mb-2">B. User Content</h3>
            <p className="text-muted-foreground leading-relaxed mb-2">
              We collect the data you input to use the App's features, including:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 mb-4">
              <li>Task names, details, and tags.</li>
              <li>Focus timer logs and duration.</li>
              <li>Mood & Status Logs: Information you voluntarily provide about your daily sentiment ("How's your day going?").</li>
              <li>"The ONE Thing" priorities.</li>
            </ul>

            <h3 className="text-lg font-medium mb-2">C. Payment Information</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              If you subscribe to Luma Pro, our third-party payment processor (e.g., Stripe, Apple App Store) processes your payment. We do not store your full credit card details.
            </p>

            <h3 className="text-lg font-medium mb-2">D. Usage & Device Data</h3>
            <p className="text-muted-foreground leading-relaxed">
              We may collect non-personal data such as device type, operating system version, and general usage statistics (e.g., features used, crash reports) to improve app stability.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">3. How We Use Your Information</h2>
            <p className="text-muted-foreground leading-relaxed mb-2">We use your data to:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              <li>Provide, maintain, and improve the App's functionality.</li>
              <li>Sync your data securely across your devices.</li>
              <li>Generate your personal productivity insights and growth charts.</li>
              <li>Process subscription payments and manage your account status.</li>
              <li>Send you technical notices, updates, and support messages.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">4. Data Sharing and Disclosure</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We do not sell your personal data to advertisers. We only share data in the following circumstances:
            </p>
            <p className="text-muted-foreground leading-relaxed mb-2">
              <strong className="text-foreground">Service Providers:</strong> We use trusted third-party services (e.g., Supabase for database hosting, Stripe for payments) to operate the App. These providers have access to your information only to perform specific tasks on our behalf and are obligated to protect it.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Legal Compliance:</strong> We may disclose your information if required by law or to protect our rights and safety.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">5. Data Retention & Deletion</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We retain your personal information and user content for as long as your account is active. You may request deletion of your account at any time via the App's "Profile {'>'} Privacy" settings.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              <strong className="text-foreground">Upon account deletion:</strong>
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4">
              <li>Your authentication credentials (email, password) are immediately and permanently removed.</li>
              <li>Personal information (display name, avatar) is permanently deleted.</li>
              <li>Tasks, notes, and daily status updates are permanently deleted.</li>
              <li>Focus session data is anonymized (user identifiers removed) to preserve aggregate statistics for app improvement.</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Financial Records:</strong> We are legally required to retain certain financial records (transaction IDs, amounts, dates) for tax and accounting purposes for 5-7 years. These records are maintained by our payment processors (Stripe, Apple) and are not linked to your personal identity after account deletion.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">6. Your Rights (GDPR & CCPA)</h2>
            <p className="text-muted-foreground leading-relaxed mb-2">
              Depending on your location, you have the right to:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              <li><strong className="text-foreground">Access:</strong> Request a copy of the personal data we hold about you.</li>
              <li><strong className="text-foreground">Correction:</strong> Update inaccurate information via your Profile settings.</li>
              <li><strong className="text-foreground">Deletion:</strong> Request the erasure of your account and data ("Right to be Forgotten").</li>
              <li><strong className="text-foreground">Portability:</strong> Request your data in a structured, commonly used format.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">7. Security</h2>
            <p className="text-muted-foreground leading-relaxed">
              We implement industry-standard security measures (including encryption in transit and at rest) to protect your data. However, no method of transmission over the Internet is 100% secure.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">8. Children's Privacy</h2>
            <p className="text-muted-foreground leading-relaxed">
              The App is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">9. Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have questions about this Privacy Policy, please contact us at:{' '}
              <a href="mailto:support@luma-todo.xyz" className="text-primary hover:underline">
                support@luma-todo.xyz
              </a>
            </p>
          </section>
        </article>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/50 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Luma. Focus on what matters.
          </p>
        </div>
      </footer>
    </div>
  );
}
